import { supabase } from './supabase';
import { Database } from '../types/database.types';
import { isCdnUploadEnabled, toMediaUrl } from './mediaUrl';
import { uploadViaCdn } from './uploadMedia';
import { createImageThumbnailBlob, shouldReuseOriginalAsThumb } from './imageThumbnail';

export type MediaAsset = Database['public']['Tables']['media_assets']['Row'];
export type MediaAssetType = 'image' | 'video';

export const MEDIA_STATUS_APPROVED = 'approved';
export const MEDIA_STATUS_PENDING = 'pending_admin_approval';

export function isMediaApproved(asset: { status?: string | null }): boolean {
  return asset.status === MEDIA_STATUS_APPROVED;
}

/** Admin/fin_ops can Liberar — só eles veem pendentes no picker e na galeria. */
export function canReviewMediaAssets(role?: string | null): boolean {
  return role === 'admin' || role === 'fin_ops';
}

/** Admin vê tudo. Demais membros veem aprovados + uploads manuais (sem generation_id). */
export function galleryListStatus(role?: string | null): 'approved' | 'all' | 'usable' {
  return canReviewMediaAssets(role) ? 'all' : 'usable';
}

export async function getUserOrgId(userId: string): Promise<string | null> {
  const { data } = await supabase
    .from('organization_members')
    .select('org_id')
    .eq('user_id', userId)
    .single();
  return data?.org_id ?? null;
}

export async function fetchOrgMediaAssets(
  orgId: string,
  options?: { assetType?: MediaAssetType | 'all'; status?: string | 'all' },
): Promise<MediaAsset[]> {
  let query = supabase
    .from('media_assets')
    .select('*')
    .eq('org_id', orgId)
    .order('created_at', { ascending: false });

  if (options?.assetType && options.assetType !== 'all') {
    query = query.eq('asset_type', options.assetType);
  }
  if (options?.status && options.status !== 'all') {
    if (options.status === 'usable') {
      query = query.or('status.eq.approved,generation_id.is.null');
    } else {
      query = query.eq('status', options.status);
    }
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map((row) => ({
    ...row,
    video_url: toMediaUrl(row.video_url),
    thumbnail_url: row.thumbnail_url ? toMediaUrl(row.thumbnail_url) : row.thumbnail_url,
  }));
}

export function galleryThumbSrc(asset: Pick<MediaAsset, 'video_url' | 'thumbnail_url'>): string {
  return toMediaUrl(asset.thumbnail_url) || toMediaUrl(asset.video_url);
}

const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  jfif: 'image/jpeg',
  jiff: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  bmp: 'image/bmp',
  heic: 'image/heic',
  heif: 'image/heif',
  avif: 'image/avif',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  m4v: 'video/mp4',
};

/** Formats PoYo / image APIs often reject — re-encode to PNG before gallery upload. */
const CONVERT_TO_PNG_EXT = new Set(['jfif', 'jiff', 'bmp', 'tif', 'tiff', 'heic', 'heif']);

/** Windows/Cursor often send File.type vazio ou image/jpg. */
export function normalizeGalleryFile(file: File): File {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  let type = (file.type || '').toLowerCase().split(';')[0].trim();
  if (type === 'image/jpg' || type === 'image/pjpeg' || type === 'image/jfif') type = 'image/jpeg';
  if (!type && MIME_BY_EXT[ext]) type = MIME_BY_EXT[ext];
  if (type === file.type) return file;
  return new File([file], file.name, { type });
}

/**
 * Decode via the browser and export PNG so providers (PoYo) get a supported container.
 * Used for .jfif/.jiff/.bmp/etc before gallery upload.
 */
export async function ensureProviderSafeImageFile(file: File): Promise<File> {
  const normalized = normalizeGalleryFile(file);
  if (!normalized.type.startsWith('image/')) return normalized;
  const ext = (normalized.name.split('.').pop() || '').toLowerCase();
  const needsConvert = CONVERT_TO_PNG_EXT.has(ext)
    || /jfif|jiff/i.test(normalized.type)
    || normalized.type === 'image/bmp'
    || normalized.type === 'image/tiff';
  if (!needsConvert) return normalized;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(normalized);
  } catch {
    throw new Error('Não foi possível ler esta imagem. Salve como PNG ou JPG e tente de novo.');
  }
  try {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, bitmap.width);
    canvas.height = Math.max(1, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas indisponível para converter a imagem.');
    ctx.drawImage(bitmap, 0, 0);
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (next) => (next ? resolve(next) : reject(new Error('Falha ao converter a imagem para PNG.'))),
        'image/png',
      );
    });
    const base = normalized.name.replace(/\.[^.]+$/i, '') || 'imagem';
    return new File([blob], `${base}.png`, { type: 'image/png' });
  } finally {
    bitmap.close();
  }
}

export function dataUrlToFile(dataUrl: string, filename: string): File {
  const match = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
  if (!match) throw new Error('Imagem inválida para salvar na galeria.');
  const mime = match[1] || 'image/jpeg';
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const ext = mime.includes('png') ? 'png' : mime.includes('webp') ? 'webp' : 'jpg';
  const safeName = filename.includes('.') ? filename : `${filename}.${ext}`;
  return new File([bytes], safeName, { type: mime });
}

async function requireCdnUpload(
  orgId: string,
  file: Blob,
  opts: { contentType?: string; filename?: string; purpose?: 'gallery' | 'frame' },
): Promise<string> {
  if (!isCdnUploadEnabled()) {
    throw new Error('Upload de mídia indisponível: CDN/S3 desligado neste ambiente (REACT_APP_MEDIA_CDN_URL).');
  }
  try {
    const uploaded = await uploadViaCdn({
      organizationId: orgId,
      file,
      contentType: opts.contentType || (file instanceof File ? file.type : undefined),
      filename: opts.filename || (file instanceof File ? file.name : undefined),
      purpose: opts.purpose || 'gallery',
    });
    return uploaded.publicUrl;
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(`Falha no upload para o S3/CDN: ${detail}`);
  }
}

export type MediaImagePurpose = 'avatar' | 'photo';

const PURPOSE_LINE = /^purpose:(avatar|photo)$/i;

export function imagePurposeFromAsset(asset: {
  generation_context?: string | null;
}): MediaImagePurpose | undefined {
  const match = (asset.generation_context || '').split('\n').map((l) => l.trim()).find((l) => PURPOSE_LINE.test(l));
  if (!match) return undefined;
  return /avatar/i.test(match) ? 'avatar' : 'photo';
}

export function applyImagePurpose(context: string | null | undefined, purpose: MediaImagePurpose): string {
  const rest = (context || '').split('\n').map((l) => l.trim()).filter((l) => l && !PURPOSE_LINE.test(l)).join('\n');
  const tag = purpose === 'avatar' ? 'purpose:avatar' : 'purpose:photo';
  return rest ? `${tag}\n${rest}` : tag;
}

export async function setMediaImagePurpose(
  assetId: string,
  purpose: MediaImagePurpose,
  currentContext?: string | null,
): Promise<string> {
  const generation_context = applyImagePurpose(currentContext, purpose);
  const { error } = await supabase
    .from('media_assets')
    .update({ generation_context })
    .eq('id', assetId);
  if (error) throw new Error(`Falha ao marcar a imagem: ${error.message}`);
  return generation_context;
}

/**
 * Upload a local file and insert into media_assets (pending until admin/fin_ops Liberar).
 * P7: always S3 + CloudFront — never Supabase Storage.
 */
export async function uploadToGallery(
  orgId: string,
  file: File,
  options?: { title?: string; status?: string; purpose?: MediaImagePurpose; generation_context?: string | null },
): Promise<MediaAsset> {
  let upload = normalizeGalleryFile(file);
  const isVideo = upload.type.startsWith('video/');
  const isImage = upload.type.startsWith('image/');
  if (!isVideo && !isImage) {
    throw new Error('Apenas imagens ou vídeos são suportados. Se o arquivo não tiver extensão (.jpg, .png, .mp4), renomeie e tente de novo.');
  }
  if (isImage) {
    upload = await ensureProviderSafeImageFile(upload);
  }
  file = upload;

  const assetType: MediaAssetType = isVideo ? 'video' : 'image';
  const publicUrl = await requireCdnUpload(orgId, file, {
    contentType: file.type,
    filename: file.name,
    purpose: 'gallery',
  });
  let thumbnailUrl: string | null = null;

  if (isImage) {
    try {
      thumbnailUrl = shouldReuseOriginalAsThumb(file)
        ? publicUrl
        : await uploadImageThumbnail(orgId, file, file.name);
    } catch (err) {
      console.warn('Gallery thumbnail upload failed:', err);
    }
  }

  const { data, error: insertError } = await supabase
    .from('media_assets')
    .insert([{
      org_id: orgId,
      video_url: publicUrl,
      thumbnail_url: thumbnailUrl,
      asset_type: assetType,
      title: options?.title || file.name.replace(/\.[^.]+$/, ''),
      status: options?.status || MEDIA_STATUS_APPROVED,
      generation_context: options?.purpose
        ? applyImagePurpose(options.generation_context, options.purpose)
        : (options?.generation_context || null),
    }])
    .select()
    .single();

  if (insertError) throw new Error(`Falha ao registrar na galeria: ${insertError.message}`);
  return {
    ...data,
    video_url: toMediaUrl(data.video_url),
    thumbnail_url: data.thumbnail_url ? toMediaUrl(data.thumbnail_url) : data.thumbnail_url,
  };
}

/** Rename gallery assets of the org whose URL matches (members allowed; RPC checks membership). */
export async function renameMediaAssetsByUrl(orgId: string, urls: string[], title: string): Promise<number> {
  const list = urls.filter(Boolean).slice(0, 50);
  if (!orgId || !list.length || !title.trim()) return 0;
  const { data, error } = await supabase.rpc('rename_media_assets_by_url', {
    p_org_id: orgId,
    p_urls: list,
    p_title: title,
  });
  if (error) throw new Error(`Falha ao renomear na galeria: ${error.message}`);
  return typeof data === 'number' ? data : 0;
}

/** Fetch a remote media URL as a data URL (for staging/drone frame inputs). */
export async function urlToDataUrl(url: string): Promise<string> {
  const resp = await fetch(toMediaUrl(url));
  if (!resp.ok) throw new Error('Não foi possível carregar a mídia da galeria.');
  const blob = await resp.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Falha ao converter mídia.'));
    reader.readAsDataURL(blob);
  });
}

export function isImageAsset(asset: MediaAsset): boolean {
  return (asset.asset_type || 'video') === 'image';
}

export function isVideoAsset(asset: MediaAsset): boolean {
  return (asset.asset_type || 'video') === 'video';
}

/** Face photo marked as Avatar, or Kling Avatar video jobs. */
export function isAvatarAsset(asset: Pick<MediaAsset, 'asset_type' | 'title' | 'generation_context'>): boolean {
  if (imagePurposeFromAsset(asset) === 'avatar') return true;
  if ((asset.asset_type || 'video') === 'image') return false;
  const hay = `${asset.generation_context || ''}\n${asset.title || ''}`;
  return /kling-avatar|poyo-kling-avatar|(^|\b)avatar(\b|:)/i.test(hay);
}

export function mediaUrlKey(url?: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).pathname.replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.split('?')[0].toLowerCase();
  }
}

export function findMediaAssetByUrl(assets: MediaAsset[], url?: string | null): MediaAsset | undefined {
  const key = mediaUrlKey(url);
  if (!key) return undefined;
  return assets.find((asset) => mediaUrlKey(asset.video_url) === key);
}

export async function findOrgMediaAssetByUrl(orgId: string, url?: string | null): Promise<MediaAsset | undefined> {
  const key = mediaUrlKey(url);
  if (!orgId || !key) return undefined;
  const assets = await fetchOrgMediaAssets(orgId, { assetType: 'video', status: 'all' });
  return findMediaAssetByUrl(assets, url);
}

async function persistThumbnailUrl(assetId: string, thumbnailUrl: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('set_media_asset_thumbnail', {
    p_asset_id: assetId,
    p_thumbnail_url: thumbnailUrl,
  });
  if (error) throw error;
  return Boolean(data);
}

async function uploadImageThumbnail(orgId: string, source: Blob, filename?: string): Promise<string | null> {
  if (shouldReuseOriginalAsThumb(source)) return null;
  const thumb = await createImageThumbnailBlob(source);
  const thumbName = `${(filename || 'thumb').replace(/\.[^.]+$/, '')}-thumb.jpg`;
  return requireCdnUpload(orgId, thumb, {
    contentType: 'image/jpeg',
    filename: thumbName,
    purpose: 'gallery',
  });
}

const thumbBackfillInFlight = new Set<string>();

/** Create JPEG thumbs for existing gallery images (first visit). Original stays untouched. */
export async function backfillMissingThumbnails(
  orgId: string,
  assets: MediaAsset[],
  onUpdated?: (id: string, thumbnailUrl: string) => void,
): Promise<void> {
  const pending = assets.filter((asset) => (
    isImageAsset(asset)
    && !asset.thumbnail_url
    && asset.video_url
    && !thumbBackfillInFlight.has(asset.id)
  ));
  if (!pending.length) return;

  const queue = pending.slice(0, 24);
  const workers = 2;
  let cursor = 0;

  const runOne = async () => {
    while (cursor < queue.length) {
      const asset = queue[cursor];
      cursor += 1;
      if (thumbBackfillInFlight.has(asset.id)) continue;
      thumbBackfillInFlight.add(asset.id);
      try {
        const resp = await fetch(toMediaUrl(asset.video_url), { mode: 'cors' });
        if (!resp.ok) continue;
        const blob = await resp.blob();
        let thumbUrl: string | null = null;
        if (shouldReuseOriginalAsThumb(blob)) {
          thumbUrl = asset.video_url;
        } else {
          thumbUrl = await uploadImageThumbnail(orgId, blob, asset.title || asset.id);
        }
        if (!thumbUrl) continue;
        await persistThumbnailUrl(asset.id, thumbUrl);
        onUpdated?.(asset.id, toMediaUrl(thumbUrl));
      } catch (err) {
        console.warn('Gallery thumbnail backfill failed:', asset.id, err);
      } finally {
        thumbBackfillInFlight.delete(asset.id);
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(workers, queue.length) }, () => runOne()));
}

const GENERIC_ASSET_TITLE = /^(vídeo gerado|imagem gerada|video gerado)$/i;

function titleFromGenerationContext(ctx?: string | null): string {
  if (!ctx?.trim()) return '';
  const lines = ctx.split('\n').map((line) => line.trim()).filter((line) => line && !PURPOSE_LINE.test(line));
  const prompt = lines.length > 1 ? lines.slice(1).join(' ') : (lines[0] || '');
  const afterRef = prompt.includes('>>>.') ? prompt.replace(/^[\s\S]*>>>\.\s*/, '') : prompt;
  const cleaned = afterRef.replace(/^(one continuous[^.]*\.\s*)+/i, '').trim();
  if (!cleaned || GENERIC_ASSET_TITLE.test(cleaned) || /^(start exactly|ambient life)/i.test(cleaned)) {
    return '';
  }
  return cleaned.slice(0, 72);
}

export function assetDisplayName(asset: MediaAsset): string {
  const raw = asset.title?.trim() || '';
  if (raw && !GENERIC_ASSET_TITLE.test(raw)) return raw;
  const fromContext = titleFromGenerationContext(asset.generation_context);
  if (fromContext) return fromContext;
  const kind = isImageAsset(asset) ? 'Imagem' : 'Vídeo';
  const date = asset.created_at ? new Date(asset.created_at).toLocaleDateString('pt-BR') : '';
  return date ? `${kind} · ${date}` : kind;
}
